import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { VehicleDataService } from '../../pages/register/vehicle/create-vehicle/vehicle-data.service';
import { ConfigUtils } from '../../utils/config';
import { PermissionsService } from '../token/permissions.service';

/**
 * Interfaz para la respuesta de la autenticación
 */
export interface AuthResponse {
  email: string;
  first_logon: number | boolean;
  fullname: string;
  id: number;
  id_employee: number;
  message_type: string;
  position: string;
  role_description: string;
  role_name: string;
  role_permits: string;
  status: number;
  token: string;
  username: string;
}

/**
 * Interfaz para las credenciales de login
 */
export interface LoginCredentials {
  username: string;
  password: string;
}

/**
 * Clase para manejar el almacenamiento seguro de datos de autenticación
 * Encapsula el acceso a sessionStorage para mayor seguridad
 */
class SecureStorage {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  /**
   * Guarda el token de autenticación
   * @param token Token JWT
   */
  saveToken(token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Obtiene el token almacenado
   * @returns Token JWT o null si no existe
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Guarda los datos del usuario
   * @param user Datos del usuario
   */
  saveUser(user: AuthResponse): void {
    // Eliminamos el token del objeto usuario para no duplicarlo
    const { token, ...userWithoutToken } = user;
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(userWithoutToken));
  }

  /**
   * Obtiene los datos del usuario
   * @returns Datos del usuario o null si no existe
   */
  getUser(): AuthResponse | null {
    const userStr = sessionStorage.getItem(this.USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr) as AuthResponse;
    } catch (e) {
      console.error('Error parsing user data from sessionStorage', e);
      return null;
    }
  }

  /**
   * Elimina todos los datos de autenticación
   */
  clear(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
  }
}

/**
 * Servicio de autenticación
 * Maneja el proceso de login/logout y almacena el token JWT
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = ConfigUtils.getApiUrl();
  private secureStorage = new SecureStorage();
  private permissionsService = inject(PermissionsService);

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasToken()
  );
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private currentUserSubject = new BehaviorSubject<AuthResponse | null>(
    this.getUserFromStorage()
  );
  public currentUser$ = this.currentUserSubject.asObservable();

  /**
   * Constructor del servicio
   */
  constructor(
    private http: HttpClient,
    private router: Router,
    private vehicleDataService: VehicleDataService
  ) {}

  /**
   * Realiza el proceso de login
   * @param credentials Credenciales del usuario
   * @returns Observable con la respuesta de autenticación
   */
  login(credentials: LoginCredentials): Observable<AuthResponse> {
    const headers = new HttpHeaders({ 'Skip-Loading': 'true' });
    return this.http
      .post<AuthResponse>(
        `${this.apiUrl}/authentications/users/login`,
        credentials,
        { headers }
      )
      .pipe(
        tap((response) => {
          if (response?.token) {
            this.setSession(response);
          }
        })
      );
  }

  /**
   * Cambia la contraseña del usuario
   * @param request - Datos de la nueva contraseña
   * @returns Observable con la respuesta del cambio de contraseña
   */
  changePassword(request: {
    id: number;
    username: string;
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Observable<any> {
    return this.http
      .patch<any>(
        `${this.apiUrl}/authentications/users/change/password`,
        request
      )
      .pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  /**
   * Cierra la sesión del usuario
   */
  logout(): void {
    this.vehicleDataService.clearCache();

    this.secureStorage.clear();
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  /**
   * Obtiene el token de autenticación
   * @returns El token JWT o null si no existe
   */
  getToken(): string | null {
    return this.secureStorage.getToken();
  }

  /**
   * Almacena la información de la sesión
   * @param authResult Respuesta de autenticación
   */
  private setSession(authResult: AuthResponse): void {
    this.secureStorage.saveToken(authResult.token);
    this.secureStorage.saveUser(authResult);
    this.isAuthenticatedSubject.next(true);
    this.currentUserSubject.next(authResult);
    // Recargar permisos después del login
    this.permissionsService.loadPermissions();
  }

  /**
   * Verifica si hay un token almacenado
   * @returns true si existe un token
   */
  private hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Obtiene la información del usuario desde sessionStorage
   * @returns Datos del usuario o null
   */
  private getUserFromStorage(): AuthResponse | null {
    return this.secureStorage.getUser();
  }

  /**
   * Obtiene los datos del usuario actual
   * @returns Datos del usuario o null
   */
  getCurrentUser(): AuthResponse | null {
    return this.getUserFromStorage();
  }

  /**
   * Actualiza los datos del usuario en el almacenamiento local
   * @param userData Datos actualizados del usuario
   */
  updateUserData(userData: AuthResponse): void {
    this.secureStorage.saveUser(userData);
    this.currentUserSubject.next(userData);
  }

  /**
   * Verifica si el usuario está autenticado
   * @returns true si el usuario está autenticado, false en caso contrario
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;

    return Date.now() < exp;
  }

  /**
   * Cierra la sesión del usuario
   */
  signOut(): void {
    this.vehicleDataService.clearCache();
    this.secureStorage.clear();
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }
}
